import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Printer, Download, ArrowLeft } from "lucide-react";
import { usePdfRenderer } from "../../hooks/usePdfRenderer";
import Header from "../common/Header";
import MedicalRecordPDF from "../../pdf/MedicalRecordPDF";

const RecordView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminContext = location.pathname.includes("/admin/");
  const { download, print } = usePdfRenderer();
  
  // Données mockées pour l'instant
  const mockRecord: any = {
    id: 1,
    patientFullName: "Jean Dupont",
    patientPhone: "0123456789",
    doctorFullName: "Dr. Martin",
    dateCreation: new Date().toISOString(),
    symptoms: "Douleur abdominale",
    symptomDuration: "2 jours",
    currentMedication: "Aucun",
    preexistingCondition: "Aucune",
    familyHistory: "Aucun",
    allergies: "Aucune",
    assessment: "Examen clinique normal",
    recommendation: "Repos et surveillance"
  };

  const handleBack = () => {
    if (isAdminContext) {
      navigate("/admin/dashboard/patients");
    } else {
      navigate("/doctor/dashboard");
    }
  };

  const handlePrint = () => {
    if (mockRecord) {
      print(
        <MedicalRecordPDF
          record={mockRecord}
          patientPhone={mockRecord.patientPhone}
          doctorFullName={mockRecord.doctorFullName}
        />
      );
    }
  };

  const handleDownload = () => {
    if (mockRecord) {
      download(
        <MedicalRecordPDF
          record={mockRecord}
          patientPhone={mockRecord.patientPhone}
          doctorFullName={mockRecord.doctorFullName}
        />,
        `dossier_medical_${mockRecord.patientFullName.replace(/[^a-zA-Z0-9]/g, "_")}_${Date.now()}.pdf`
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Header
        title="Dossiers Médicaux"
        subtitle="Consultez les dossiers médicaux des patients."
        icon={<ArrowLeft className="w-6 h-6 text-white" />}
        role={isAdminContext ? "admin" : "doctor"}
        actionLink={isAdminContext ? "/admin/dashboard/patients" : "/doctor/dashboard"}
        actionText="Retour"
        description=""
        refreshAction={handleBack}
        refreshText="Retour"
      />
      
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 md:p-8 space-y-8">
            <div className="flex justify-end items-center gap-2">
              <button
                onClick={handlePrint}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
              >
                <Printer size={16} /> Imprimer
              </button>
              <button
                onClick={handleDownload}
                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
              >
                <Download size={16} /> Télécharger Dossier
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                  Informations Patient
                </h3>
                <p>
                  <strong>Nom:</strong> {mockRecord.patientFullName}
                </p>
                <p>
                  <strong>Téléphone:</strong> {mockRecord.patientPhone}
                </p>
                <p>
                  <strong>Date de création:</strong>{" "}
                  {new Date(mockRecord.dateCreation).toLocaleDateString("fr-FR")}
                </p>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                  Médecin
                </h3>
                <p>
                  <strong>Nom du Médecin:</strong> {mockRecord.doctorFullName}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                  Symptômes
                </h3>
                <p>
                  <strong>Symptôme:</strong> {mockRecord.symptoms}
                </p>
                <p>
                  <strong>Durée des symptômes:</strong> {mockRecord.symptomDuration}
                </p>
                <p>
                  <strong>Médicaments actuels:</strong> {mockRecord.currentMedication}
                </p>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                  Antécédents
                </h3>
                <p>
                  <strong>Conditions préexistantes:</strong> {mockRecord.preexistingCondition}
                </p>
                <p>
                  <strong>Antécédents familiaux:</strong> {mockRecord.familyHistory}
                </p>
                <p>
                  <strong>Allergies:</strong> {mockRecord.allergies}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-indigo-700 border-b pb-2">
                Évaluation
              </h3>
              <p>
                <strong>Évaluation:</strong> {mockRecord.assessment}
              </p>
              <p>
                <strong>Recommandation:</strong> {mockRecord.recommendation}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordView;
